import { useLocation, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Minus } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { SEOWrapper } from "@/components/SEOWrapper";
import {
  ActionLink,
  ContextPanel,
  DeliverableList,
  InteriorHead,
  ProcessTrack,
  SectionIndex,
  SectionStrip,
  type SectionRef,
} from "@/components/luminous/interior";
import { Reveal } from "@/components/luminous/primitives";
import { ServiceDiagram, diagramForService } from "@/components/service-page/ServiceDiagram";
import { detectLanguage } from "@/utils/routes";
import { insights } from "@/data/insights";
import type { MoneyPageContent } from "@/data/money-pages/types";

const BASE_URL = "https://luisroc.com";

interface MoneyPageProps {
  content: MoneyPageContent;
}

const ui = {
  es: {
    home: "Inicio",
    services: "Servicios",
    onThisPage: "En esta página",
    context: "Por qué importa",
    problems: "Problemas frecuentes",
    scope: "Alcance y entregables",
    process: "Proceso",
    forWho: "Para quién",
    related: "Recursos y servicios relacionados",
    faq: "Preguntas",
    contact: "Siguiente paso",
    impact: "Impacto",
    notIncluded: "No incluye",
    quickScope: "Alcance en una línea",
    startHere: "Empezar por aquí",
    consequence: "Si no se resuelve",
  },
  en: {
    home: "Home",
    services: "Services",
    onThisPage: "On this page",
    context: "Why it matters",
    problems: "Common problems",
    scope: "Scope and deliverables",
    process: "Process",
    forWho: "Who it is for",
    related: "Related resources and services",
    faq: "Questions",
    contact: "Next step",
    impact: "Impact",
    notIncluded: "Not included",
    quickScope: "Scope in one line",
    startHere: "Start here",
    consequence: "If left unresolved",
  },
} as const;

/**
 * Renderizador compartido de las páginas comerciales de servicio.
 * Composición interior propia (no repite el hero de la Home): encabezado
 * compacto, columna de lectura, panel contextual con índice y alcance,
 * diagrama explicativo por familia de servicio y contacto al final.
 * El idioma se deriva de la URL, nunca del estado de React.
 */
export const MoneyPage = ({ content }: MoneyPageProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const language = detectLanguage(location.pathname);
  const t = content[language];
  const l = ui[language];

  const esUrl = `${BASE_URL}${content.esPath}`;
  const enUrl = `${BASE_URL}${content.enPath}`;
  const url = language === "es" ? esUrl : enUrl;
  const path = language === "es" ? content.esPath : content.enPath;
  const servicesPath = language === "es" ? "/servicios" : "/en/services";

  // Spoke → hub: piezas de la Search Library que desarrollan este servicio.
  const libraryLinks = insights
    .filter((i) => i.moneyPage[language] === path)
    .map((i) => ({
      to: i.path[language],
      label: i[language].h1,
      description: i[language].description,
    }));

  const handleLanguageChange = (lang: "es" | "en") => {
    if (lang !== language) {
      navigate(lang === "es" ? content.esPath : content.enPath);
    }
  };

  const sections: SectionRef[] = [
    { id: "contexto", label: l.context },
    { id: "problemas", label: l.problems },
    { id: "alcance", label: l.scope },
    { id: "proceso", label: l.process },
    { id: "para-quien", label: l.forWho },
    ...(t.faq ? [{ id: "preguntas", label: l.faq }] : []),
    { id: "contacto", label: l.contact },
  ];

  const relatedLinks = [...t.related.links, ...libraryLinks];

  return (
    <SEOWrapper
      title={t.metaTitle}
      description={t.metaDescription}
      keywords={t.keywords}
      url={url}
      esUrl={esUrl}
      enUrl={enUrl}
      language={language}
      pageType="service"
      localBusiness={false}
      structuredData={{
        type: "service",
        data: {
          name: content.serviceType,
          description: t.metaDescription,
          areaServed: "United States",
          serviceType: content.serviceType,
          url,
        },
      }}
      summary={t.llmSummary}
      keyPoints={t.keyPoints}
    >
      <div className="min-h-screen bg-[color:var(--v3-mineral)]">
        <Header language={language} onLanguageChange={handleLanguageChange} />

        <main>
          <InteriorHead
            crumbs={[
              { label: l.home, to: language === "es" ? "/" : "/en" },
              { label: l.services, to: servicesPath },
              { label: t.breadcrumbLabel },
            ]}
            eyebrow={content.serviceType}
            title={t.h1}
            definition={t.explanation.definition}
            audienceLabel={`${t.forWho.idealFor.title}:`}
            audience={t.forWho.idealFor.items.slice(0, 3).join(" · ")}
            actions={
              <>
                <ActionLink to={t.ctaPrimary.to}>
                  {t.ctaPrimary.label}
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </ActionLink>
                <ActionLink to={t.ctaSecondary.to} variant="ghost">
                  {t.ctaSecondary.label}
                </ActionLink>
              </>
            }
            facts={t.summary.map((p) => ({ label: p.label, value: p.text }))}
          />

          <SectionStrip items={sections} label={l.onThisPage} />

          <div className="v3-shell grid gap-12 py-12 lg:grid-cols-12 lg:gap-16 lg:py-16">
            {/* Panel contextual: navegación de la página y alcance resumido. */}
            <div className="hidden lg:col-span-4 lg:col-start-9 lg:row-start-1 lg:block">
              <div className="lg:sticky lg:top-[calc(var(--v3-header-h)+2rem)] lg:space-y-8">
                <SectionIndex items={sections} label={l.onThisPage} />
                <ContextPanel title={l.quickScope} sticky={false}>
                  <ul className="space-y-2.5">
                    {t.scope.includes.items.slice(0, 5).map((item) => (
                      <li key={item} className="flex gap-2.5 text-[0.9rem] leading-snug text-[color:var(--v3-ink-soft)]">
                        <Check className="mt-0.5 h-4 w-4 shrink-0" style={{ color: "var(--v3-blue)" }} aria-hidden />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={t.ctaPrimary.to}
                    className="v3-btn-ghost mt-5"
                  >
                    {l.startHere}
                  </Link>
                </ContextPanel>
              </div>
            </div>

            {/* Columna de lectura */}
            <div className="min-w-0 lg:col-span-7 lg:row-start-1 lg:space-y-16">
              <section id="contexto" className="scroll-mt-28">
                <p className="v3-micro">{l.context}</p>
                <h2 className="v3-display mt-4" style={{ fontSize: "clamp(1.5rem, 2.3vw, 2.05rem)" }}>
                  {t.explanation.title}
                </h2>
                <div className="mt-6 space-y-6" style={{ maxWidth: "68ch" }}>
                  <div>
                    <p className="text-[0.95rem] font-semibold text-[color:var(--v3-ink)]">
                      {t.explanation.whyImportant.title}
                    </p>
                    <p className="v3-body mt-2">{t.explanation.whyImportant.text}</p>
                  </div>
                  <div className="border-l-2 border-[color:var(--v3-copper)] pl-5">
                    <p className="v3-micro" style={{ color: "var(--v3-copper)" }}>
                      {l.consequence}
                    </p>
                    <p className="text-[0.95rem] font-semibold text-[color:var(--v3-ink)] mt-2">
                      {t.explanation.consequences.title}
                    </p>
                    <p className="v3-body mt-2">{t.explanation.consequences.text}</p>
                  </div>
                </div>
              </section>

              <section id="problemas" className="mt-16 scroll-mt-28 lg:mt-0">
                <p className="v3-micro">{l.problems}</p>
                <h2 className="v3-display mt-4" style={{ fontSize: "clamp(1.5rem, 2.3vw, 2.05rem)" }}>
                  {t.problems.title}
                </h2>
                <p className="v3-body mt-5">{t.problems.subtitle}</p>
                <ul className="mt-8">
                  {t.problems.items.map((problem, i) => {
                    const Icon = problem.icon;
                    return (
                      <Reveal as="li" key={problem.title} delay={i * 0.04}>
                        <div className="border-t border-[color:var(--v3-line)] py-6">
                          <p className="flex items-start gap-3 text-[1rem] font-semibold text-[color:var(--v3-ink)]">
                            <Icon className="mt-0.5 h-5 w-5 shrink-0" style={{ color: "var(--v3-blue)" }} aria-hidden />
                            {problem.title}
                          </p>
                          <p className="v3-body mt-2.5 pl-8">{problem.description}</p>
                          <p className="v3-note mt-2 pl-8">
                            <strong className="text-[color:var(--v3-copper)]">{l.impact}: </strong>
                            {problem.impact}
                          </p>
                        </div>
                      </Reveal>
                    );
                  })}
                </ul>
              </section>

              <section id="alcance" className="mt-16 scroll-mt-28 lg:mt-0">
                <p className="v3-micro">{l.scope}</p>
                <h2 className="v3-display mt-4" style={{ fontSize: "clamp(1.5rem, 2.3vw, 2.05rem)" }}>
                  {t.scope.includes.title}
                </h2>
                <div className="mt-8">
                  <DeliverableList
                    columns={1}
                    items={t.scope.includes.items.map((item) => ({ title: item }))}
                  />
                </div>
                <div className="mt-10 bg-[color:var(--v3-mineral-deep)] p-6">
                  <p className="v3-micro">{t.scope.notIncludes.title}</p>
                  <ul className="mt-4 space-y-2">
                    {t.scope.notIncludes.items.map((item) => (
                      <li key={item} className="flex gap-2.5 text-[0.9rem] text-[color:var(--v3-muted)]">
                        <Minus className="mt-1 h-3.5 w-3.5 shrink-0" aria-hidden />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>
            </div>

            {/* Proceso y diagrama: ancho completo bajo las dos columnas */}
            <div className="min-w-0 lg:col-span-12 lg:row-start-2">
              <section id="proceso" className="scroll-mt-28 border-t border-[color:var(--v3-line)] pt-12">
                <div className="grid gap-10 lg:grid-cols-12 lg:gap-16">
                  <div className="min-w-0 lg:col-span-5">
                    <p className="v3-micro">{l.process}</p>
                    <h2 className="v3-display mt-4" style={{ fontSize: "clamp(1.5rem, 2.3vw, 2.05rem)" }}>
                      {t.methodology.title}
                    </h2>
                    <p className="v3-body mt-5">{t.methodology.subtitle}</p>
                    <Link to={t.methodology.ctaLink} className="v3-btn-ghost mt-6">
                      {t.methodology.ctaText}
                    </Link>
                  </div>
                  <div className="min-w-0 lg:col-span-7">
                    <ServiceDiagram variant={diagramForService(content.id)} language={language} />
                  </div>
                </div>
                <div className="mt-12">
                  <ProcessTrack
                    steps={t.methodology.steps.map((s) => ({
                      title: s.title,
                      detail: s.description,
                    }))}
                  />
                </div>
              </section>

              <section id="para-quien" className="mt-16 scroll-mt-28">
                <p className="v3-micro">{l.forWho}</p>
                <div className="mt-6 grid gap-px bg-[color:var(--v3-line)] md:grid-cols-2">
                  {[t.forWho.idealFor, t.forWho.notFor].map((list, idx) => (
                    <div key={list.title} className="bg-[color:var(--v3-white)] p-7">
                      <p
                        className="text-[1rem] font-semibold"
                        style={{ color: idx === 0 ? "var(--v3-blue)" : "var(--v3-copper)" }}
                      >
                        {list.title}
                      </p>
                      <ul className="mt-4 space-y-3">
                        {list.items.map((item) => (
                          <li
                            key={item}
                            className="border-t border-[color:var(--v3-line)] pt-3 text-[0.92rem] leading-relaxed text-[color:var(--v3-ink-soft)] first:border-t-0 first:pt-0"
                          >
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </section>

              {relatedLinks.length > 0 ? (
                <section className="mt-16">
                  <p className="v3-micro">{l.related}</p>
                  <h2 className="v3-display mt-4" style={{ fontSize: "clamp(1.4rem, 2vw, 1.85rem)" }}>
                    {t.related.title}
                  </h2>
                  <p className="v3-body mt-4">{t.related.subtitle}</p>
                  <ul className="mt-8 grid gap-px bg-[color:var(--v3-line)] md:grid-cols-2">
                    {relatedLinks.map((link) => (
                      <li key={`${link.to}-${link.label}`} className="bg-[color:var(--v3-mineral)]">
                        <Link
                          to={link.to}
                          className="group block h-full p-6 transition-colors hover:bg-[color:var(--v3-white)] focus-visible:bg-[color:var(--v3-white)]"
                          style={{ transitionDuration: "var(--v3-t-control)" }}
                        >
                          <p className="flex items-start justify-between gap-4 text-[0.98rem] font-semibold text-[color:var(--v3-ink)]">
                            {link.label}
                            <ArrowRight
                              className="mt-1 h-4 w-4 shrink-0"
                              style={{ color: "var(--v3-blue)" }}
                              aria-hidden
                            />
                          </p>
                          <p className="v3-note mt-2">{link.description}</p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}

              {t.faq ? (
                <section id="preguntas" className="mt-16 scroll-mt-28">
                  <p className="v3-micro">{l.faq}</p>
                  <h2 className="v3-display mt-4" style={{ fontSize: "clamp(1.4rem, 2vw, 1.85rem)" }}>
                    {t.faq.title}
                  </h2>
                  <dl className="mt-8 grid gap-x-16 md:grid-cols-2">
                    {t.faq.items.map((item) => (
                      <div key={item.question} className="border-t border-[color:var(--v3-line)] py-6">
                        <dt className="text-[0.98rem] font-semibold text-[color:var(--v3-ink)]">
                          {item.question}
                        </dt>
                        <dd className="v3-body mt-2.5">{item.answer}</dd>
                      </div>
                    ))}
                  </dl>
                </section>
              ) : null}

              <section
                id="contacto"
                className="mt-16 scroll-mt-28 bg-[color:var(--v3-ink)] p-8 lg:p-12"
              >
                <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
                  <div className="lg:col-span-7">
                    <p className="v3-micro" style={{ color: "rgba(255,255,255,0.65)" }}>
                      {l.contact}
                    </p>
                    <p
                      className="mt-4 text-[1.5rem] font-bold leading-tight text-white lg:text-[2rem]"
                      style={{ letterSpacing: "-0.03em" }}
                    >
                      {language === "es"
                        ? "Reunión inicial gratuita de 30 minutos con el consultor."
                        : "Free 30-minute initial consultation with the consultant."}
                    </p>
                    <p className="mt-4 text-[0.95rem] leading-relaxed text-white/70" style={{ maxWidth: "58ch" }}>
                      {language === "es"
                        ? "Revisamos tu situación antes de proponer alcance e inversión. No se prometen posiciones, tráfico ni resultados garantizados."
                        : "We review your situation before proposing scope and investment. No rankings, traffic or guaranteed results are promised."}
                    </p>
                  </div>
                  <div className="lg:col-span-5 lg:justify-self-end">
                    <Link
                      to={t.ctaPrimary.to}
                      className="inline-flex min-h-12 items-center gap-3 bg-white px-7 text-[0.975rem] font-semibold text-[color:var(--v3-ink)] transition-colors hover:bg-[color:var(--v3-mineral)]"
                      style={{ transitionDuration: "var(--v3-t-control)" }}
                    >
                      {t.ctaPrimary.label}
                      <ArrowRight className="h-4 w-4" aria-hidden />
                    </Link>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </main>

        <Footer language={language} />
        <WhatsAppFloat />
      </div>
    </SEOWrapper>
  );
};
